import 'dayjs/locale/es';
import 'dayjs/plugin/duration';

import dayjs from 'dayjs';
import { useEffect, useState } from 'react';

import SessionIcon from '../../../app/assets/icons/session.svg?react';
import {
	DaySpan
} from './style';
import { useThemeContext } from '../../Theme/providers/ThemeContextProvider';
import { useMeetContext } from '../../Meet/providers/MeetContextProvider';
import { useTranslationsContext } from '../../Translations/providers/TranslationsContextProvider';
import { Column } from '../../../app/components/Column/Column';
import { Row } from '../../../app/components/Row/Row';
import { InfoBox } from '../../../app/components/InfoBox/InfoBox';
import { Space } from '../../../app/components/Space/style';
import { Modal } from '../../../app/components/Modal/Modal';
import { Promo } from '../../../app/components/Promo/Promo';
import { SheetModal } from '../../../app/components/SheetModal/SheetModal';
import { SheetModalHeader } from '../../../app/components/SheetModal/SheetModalHeader';
import { useIsMobile } from '../../../app/hooks/useIsMobile';

export interface MeetTimeRemainingModalProps {
	show?: boolean;
	onClose: () => void;
	onTimerFinished: () => void;
	onClick: () => void;
}

export function MeetTimeRemainingModal({
	show = false,
	onClose,
	onTimerFinished
}: Readonly<MeetTimeRemainingModalProps>) {
	const { t, i18n } = useTranslationsContext();
	const theme = useThemeContext();
	const { meet, professionalAvailable, features } = useMeetContext();
	const isMobile = useIsMobile();

	const [remainingTime, setRemainingTime] = useState('');

	useEffect(() => {
		if (!show) {
			return;
		}
		const interval = setInterval(() => {
			const millisRemaining = dayjs(meet?.startDate).diff(dayjs());
			if (millisRemaining < 0) {
				clearInterval(interval);
				onTimerFinished();
				onClose();
			}

			const formatted = dayjs(millisRemaining).format('mm:ss');
			setRemainingTime(formatted);
		}, 0);

		return () => clearInterval(interval);
	}, [show]);

	const content = () => {
		return (
			<Column $spaceBetween $fillHeight $gap={18}>
				{!professionalAvailable && (
					<>
						<div dangerouslySetInnerHTML={{ __html: t('too_early.message') }}></div>
						<Row $hcenter $align={'end'} $fillSpace $gap={20}>
							<InfoBox title={t('too_early.vc_time')}>
								<Column $hcenter $gap={6}>
									<DaySpan>{dayjs(meet?.startDate).locale(i18n.language).format('dddd')}</DaySpan>
									<span>{dayjs(meet?.startDate).format('HH:mm')}</span>
								</Column>
							</InfoBox>
							<InfoBox title={t('too_early.waiting_time')}>
								<span>{remainingTime}</span>
							</InfoBox>
						</Row>
						{features?.extraWaitingInfoMapfre && (
							<>
								<Space direction={'vertical'} size={16} />
								<Promo />
								<Space direction={'vertical'} size={16} />
							</>
						)}
					</>
				)}
			</Column>
		);
	};

	return (
		<>
			{!isMobile && (
				<Modal
					show={show}
					onClose={onClose}
					title={t('too_early.title')}
					$fitToChildren
					prepend={
						<SessionIcon
							fill={theme.colors.brand.primary[300]}
							width={25}
							height={25}
						></SessionIcon>
					}>
					{content()}
				</Modal>
			)}
			{isMobile && (
				<SheetModal
					isOpen={show}
					header={
						<SheetModalHeader
							prepend={
								<SessionIcon fill={theme.colors.brand.primary[300]} width={25} height={25} />
							}
							title={t('too_early.title')}
						/>
					}
					onClose={onClose}
				>
					{content()}
				</SheetModal>
			)}
		</>
	);
}
